import { useState } from 'react';
import { ArrowDown, ArrowUp, Mail, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAccountingContacts,
  useDeleteAccountingContact,
  useProjectRecipientSets,
  useUpdateAccountingContactRoleOrder,
} from '@/hooks';
import { AccountingContactModal } from './AccountingContactModal';
import { RoleBadge, type RecipientRole } from '@/components/invoices/RoleBadge';
import type { AccountingContact } from '@/lib/schemas';

/**
 * The TO/CC role each person will have on a new invoice, in list order.
 *
 * The server stores a role per person (V239). A person with no stored role (written by older code) follows the
 * old rule: the first person is TO unless somebody else is already TO. A list with no TO always makes the
 * first person TO. Same rule as `_accounting_contacts_to_recipients` on the server, so this list never says
 * something different from the invoice.
 */
export function effectiveRoles(contacts: AccountingContact[]): RecipientRole[] {
  const hasStoredTo = contacts.some((c) => c.accounting_role === 'to');
  const roles: RecipientRole[] = contacts.map((c, i) =>
    c.accounting_role === 'to' || c.accounting_role === 'cc'
      ? c.accounting_role
      : i === 0 && !hasStoredTo
        ? 'to'
        : 'cc',
  );
  if (roles.length > 0 && !roles.includes('to')) roles[0] = 'to';
  return roles;
}

interface AccountingContactsListProps {
  orgId: string;
  establishmentId?: number;
  /** Show override notice for establishment-level contacts */
  overrideNotice?: boolean;
  /** Override the CollapsibleSection title (default: "Accounting Contacts") */
  title?: string;
  testId?: string;
}

export function AccountingContactsList({
  orgId,
  establishmentId,
  overrideNotice = false,
  title = 'Accounting Contacts',
  testId,
}: AccountingContactsListProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<AccountingContact | null>(null);
  const [deletingContactId, setDeletingContactId] = useState<string | null>(null);

  const { data } = useAccountingContacts(orgId, establishmentId);
  const deleteContact = useDeleteAccountingContact();
  const updateRoleOrder = useUpdateAccountingContactRoleOrder();
  // Projects with their own saved contacts only matter for the client's own list, not a DBA's.
  const isClientLevel = establishmentId === undefined;
  const { data: projectSetsData } = useProjectRecipientSets(orgId, isClientLevel);

  const contacts: AccountingContact[] = data?.contacts ?? [];
  const count = contacts.length;
  const roles = effectiveRoles(contacts);
  const projectSets = isClientLevel ? projectSetsData?.projects ?? [] : [];

  const handleEdit = (contact: AccountingContact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleModalClose = (open: boolean) => {
    setShowModal(open);
    if (!open) setEditingContact(null);
  };

  const handleDeleteConfirm = () => {
    if (!deletingContactId) return;
    deleteContact.mutate({
      organizationId: orgId,
      contactId: deletingContactId,
      establishmentId,
    });
    setDeletingContactId(null);
  };

  return (
    <>
      <CollapsibleSection
        title={title}
        icon={<Mail className="w-5 h-5 text-gray-500" />}
        defaultOpen={false}
        summary={count > 0 ? `${count} ${count === 1 ? 'contact' : 'contacts'}` : undefined}
        testId={testId}
      >
        {overrideNotice && (
          <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-3">
            These contacts apply to invoices for this DBA
          </p>
        )}

        {contacts.length > 0 && (
          <p className="text-sm text-slate-600 mb-3" data-testid="default-contacts-intro">
            {isClientLevel
              ? 'New invoices for this client go to the people below, unless a project has its own saved contacts. TO receives the invoice; CC gets a copy.'
              : 'New invoices for this DBA go to the people below. TO receives the invoice; CC gets a copy.'}
          </p>
        )}

        {contacts.length > 0 ? (
          <div className="space-y-2">
            {contacts.map((contact, index) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                role={roles[index]}
                isFirst={index === 0}
                isLast={index === contacts.length - 1}
                busy={updateRoleOrder.isPending}
                onEdit={() => handleEdit(contact)}
                onDelete={() => setDeletingContactId(contact.id)}
                onRoleChange={(role) =>
                  updateRoleOrder.mutate({ organizationId: orgId, contactId: contact.id, accounting_role: role })
                }
                onMove={(move) =>
                  updateRoleOrder.mutate({ organizationId: orgId, contactId: contact.id, move })
                }
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500 text-sm">
              No default contacts yet. Add the people who should receive this client&apos;s invoices.
            </p>
          </div>
        )}

        {projectSets.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="project-contact-lines">
            {projectSets.map((p) => (
              <li key={p.client_scope_id} className="text-sm text-slate-600">
                {`${[p.project_number, p.project_name].filter(Boolean).join(' ')} has its own contacts: ${p.names.join(', ')}. To change who gets them, or TO and CC, open a ${p.project_number || 'project'} invoice and use the TO/CC choice or Remove next to each person.`}
              </li>
            ))}
          </ul>
        )}

        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          onClick={() => {
            setEditingContact(null);
            setShowModal(true);
          }}
        >
          Add Accounting Contact
        </Button>
      </CollapsibleSection>

      <AccountingContactModal
        open={showModal}
        onOpenChange={handleModalClose}
        organizationId={orgId}
        establishmentId={establishmentId}
        contact={editingContact ?? undefined}
      />

      <AlertDialog
        open={!!deletingContactId}
        onOpenChange={(open) => { if (!open) setDeletingContactId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Accounting Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this accounting contact? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface ContactCardProps {
  contact: AccountingContact;
  role: RecipientRole;
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRoleChange: (role: RecipientRole) => void;
  onMove: (move: 'up' | 'down') => void;
}

function ContactCard({ contact, role, isFirst, isLast, busy, onEdit, onDelete, onRoleChange, onMove }: ContactCardProps) {
  return (
    <div
      className="border rounded-lg p-3 bg-white shadow-sm flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
      data-testid={`default-contact-${contact.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <RoleBadge role={role} />
          <span className="font-medium text-sm">{contact.name}</span>
          {contact.title && (
            <span className="text-xs text-gray-500">{contact.title}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {/* v0.14.7 (#176, duality-class guard): emails can be null on an
              in-place general→accounting flip; `null.map` would crash the list.
              Optional-chain so the card renders with zero badges instead. */}
          {(contact.emails ?? []).map((email) => (
            <Badge key={email} variant="secondary" className="text-xs font-normal">
              {email}
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 sm:shrink-0 items-center">
        <select
          aria-label={`Role for ${contact.name}`}
          className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
          value={role}
          disabled={busy}
          onChange={(e) => onRoleChange(e.target.value as RecipientRole)}
        >
          <option value="to">Invoices go to (TO)</option>
          <option value="cc">Copied on (CC)</option>
        </select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onMove('up')}
          disabled={busy || isFirst}
          aria-label="Move up in the list"
          title="Move up in the list"
        >
          <ArrowUp className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onMove('down')}
          disabled={busy || isLast}
          aria-label="Move down in the list"
          title="Move down in the list"
        >
          <ArrowDown className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Edit contact">
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-gray-400 hover:text-red-500"
          aria-label="Delete contact"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
